'use client'

import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale, useTranslations } from 'next-intl';
import { FormEventHandler, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Agreements } from './Agreements';

import { AnalyticsParams, pushSimpleGAEvent } from '@components/Analytics/Google/GAScript';
import { Button, EmailInput, Input } from '@components/Form';
import { FeatureIsActive } from '@components/Only/FeatureIsActive';
import Loader from '@components/root/Loader';
import Separator from '@components/simple/Separator';
import { fetchApi } from '@utils/api/client';
import { useUserStore } from '@utils/user';
import SocialLogin from 'src/app/[locale]/(auth)/components/SocialLogin';


const defaultValues = {
  shortForm: true as const,
  email: '',
  marketingAgreement: false,
  commercialInside: false,
  commercialOutside: false,
};

const schema = z.object({
  shortForm: z.literal(true),
  email: z.string().email(),
  marketingAgreement: z.boolean().refine((value) => value === true),
  commercialInside: z.boolean().refine((value) => value === true),
  commercialOutside: z.boolean(),
});


type SchemaType = z.infer<typeof schema>;

type FormProps = {
  title?: string;
  subtitle?: string;
  isLoggedIn: boolean;
  actionLabel?: string;
  analyticsData?: AnalyticsParams;
  setIsLoginFormTriggered: (state: boolean) => void;
  onUserCheck: (isActive: boolean) => void;
}

export const NewsletterSubscribeForm = ({ title, subtitle, actionLabel, isLoggedIn, analyticsData, setIsLoginFormTriggered, onUserCheck }: FormProps) => {
  const lang = useLocale();
  const userStore = useUserStore();
  const [hasError, setHasError] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const tMain = useTranslations('main');
  const t = useTranslations('auth');

  schema.refine((data) => {
    return !data.email;
  }, {
    message: 'błędny email'
  });

  const {
    watch,
    handleSubmit,
    register,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<SchemaType>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  title = title || t('register.title');
  subtitle = subtitle || t('register.registerWithEmail');
  actionLabel = actionLabel || t('register.submit');

  const onFormSubmit: FormEventHandler = handleSubmit(async (data) => {
    // call /apis/auth/register/v2 with this email
    // if successful, hide self
    const resultRaw = await fetchApi('/auth/register/v2', {
      method: 'POST',
      onFailure: 'continue',
      body: JSON.stringify({
        ...data, data: {
          redirectUrl: window.location.href,
        }, lang
      }),
    });
    if (resultRaw.status !== 201) {
      if (resultRaw.status === 409) {
        onUserCheck(resultRaw?.data?.isActive);
      } else {
        setHasError(true);
      }
    } else {
      setIsSubmitted(true);
      await userStore.fetchUser();

      pushSimpleGAEvent('transinfo_register_step_1', {
        ...analyticsData,
        transinfo_is_logged_in: 'false',
        transinfo_is_newsletter_user: 'false',
        transinfo_user_email: data.email,
        transinfo_user_provider: 'wordpress',
        locale: lang,
        transinfo_view_category: 'register-post'
      });
    }
    return true;
  });

  return (
    <>
      {!(isSubmitted || isLoggedIn) && !isSubmitting && (
        <>
          <h3 className="mb-1 text-center text-lg font-bold lg:text-2xl">
            {title}
          </h3>
          <p className="mb-4 text-center">
            {subtitle}
          </p>
          <form onSubmit={onFormSubmit}>
            <Input
              type="hidden" {...register('shortForm')} />
            <EmailInput
              {...register('email')}
              error={!!errors.email}
              containerClass='mb-4'
              className="w-full px-4 py-2 text-sm font-light text-black bg-white rounded-md"
              placeholder={t('register.email')}
            />
            {errors.email && (
              <p className="text-xs text-red-500">{errors.email.message}</p>
            )}
            <Agreements
              secondRequired={true}
              setValue={setValue}
              watch={watch}
              register={register}
              errors={errors}
            />
            <div className='flex flex-col justify-center items-center'>
              <div className='flex flex-col justify-center items-center'>

                <Button
                  type="submit"
                  className="my-4"
                >
                  {actionLabel}
                </Button>
              </div>
            </div>
          </form>
          <Separator borderWidth={1} className='mb-4'>
            {tMain('label.or')}
          </Separator>
          <div className='text-center leading-5 flex flex-row gap-2 justify-center'>
            <div className='flex flex-col gap-2 justify-center items-center'>
              <Button
                className="text-premium-900"
                onClick={() => setIsLoginFormTriggered(true)}
              >
                {t('register.logIn')}
              </Button>
              <FeatureIsActive name='article.socialLoginFirstScreen'>
                <SocialLogin
                  size='small'
                  provider="transid"
                  label={t('socialLogin.transID')}
                />
                <SocialLogin
                  size='small' provider="linkedin" label={t('socialLogin.linkedIn')} />
              </FeatureIsActive>
            </div>
          </div>
        </>
      )}
      {isSubmitting && (
        <Loader />
      )}
      {(isSubmitted || isLoggedIn) && !hasError && (
        <div className='text-center'>
          {t('registerV2.thanksUnlocking')}
        </div>
      )}
      {hasError && (
        <div className='text-center'>
          {t('registerV2.somethingWentWrong')}
        </div>
      )}
    </>
  );
}